// Composant pour afficher une carte d'un type de licence avec ses actions
import {
  PencilIcon,
  TrashIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { LicenseType } from '../../../types/database';

interface LicenseTypeListItemProps {
  licenseType: LicenseType;
  onEdit: (licenseType: LicenseType) => void;
  onDelete: (licenseType: LicenseType) => void;
  onToggleActive: (licenseType: LicenseType) => void;
}

export function LicenseTypeListItem({
  licenseType,
  onEdit,
  onDelete,
  onToggleActive
}: LicenseTypeListItemProps) {
  const formatFeatures = (features: Record<string, boolean | string | number>) => {
    if (!features || Object.keys(features).length === 0) return 'Aucune fonctionnalité définie';
    return JSON.stringify(features, null, 2);
  };

  return (
    <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {licenseType.name}
            </h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              licenseType.is_active
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}>
              {licenseType.is_active ? 'Actif' : 'Inactif'}
            </span>
          </div>
          {licenseType.description && (
            <p className="text-gray-600 dark:text-gray-300 mb-2">
              {licenseType.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleActive(licenseType)}
            className={`p-2 rounded-lg transition-colors ${
              licenseType.is_active
                ? 'text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20'
                : 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/20'
            }`}
            title={licenseType.is_active ? 'Désactiver' : 'Activer'}
          >
            <CogIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => onEdit(licenseType)}
            className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            title="Modifier"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(licenseType)}
            className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            title="Supprimer"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <span className="text-gray-500 dark:text-gray-400">Prix:</span>
          <p className="font-medium text-gray-900 dark:text-white">
            {licenseType.price}€
          </p>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Durée:</span>
          <p className="font-medium text-gray-900 dark:text-white">
            {licenseType.duration_days ? `${licenseType.duration_days} jours` : 'À vie'}
          </p>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Appareils max:</span>
          <p className="font-medium text-gray-900 dark:text-white">
            {licenseType.max_devices}
          </p>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Créé le:</span>
          <p className="font-medium text-gray-900 dark:text-white">
            {format(new Date(licenseType.created_at), 'dd/MM/yyyy', { locale: fr })}
          </p>
        </div>
      </div>

      {licenseType.features && Object.keys(licenseType.features).length > 0 && (
        <div className="mt-4">
          <span className="text-gray-500 dark:text-gray-400 text-sm">Fonctionnalités:</span>
          <pre className="mt-1 bg-gray-100 dark:bg-gray-800 text-white rounded-lg p-3 text-xs font-mono overflow-x-auto">
            {formatFeatures(licenseType.features)}
          </pre>
        </div>
      )}
    </div>
  );
}
